import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { IconLeft } from 'react-day-picker'
import { useForm } from 'react-hook-form'
import { useRemoteClientPost } from '../../configs/http-client'
import { CatchMessageError } from '../../remote/catch-message-error'
import { useNavigateRoute, useToast } from '../hooks'
import { changePasswordForm, changePasswordScheme } from '../validators/change-password-validator'
import { sendCodeForm, sendCodeSchema } from '../validators/send-code-validator'
import { ErrorMessageInput } from './error-message-input'
import { Button } from './ui/button'
import { Input } from './ui/input'

export function ForgotPassword() {
  const { navigateTo } = useNavigateRoute()
  const [step, setStep] = useState(0)
  const { httpClientPost } = useRemoteClientPost()
  const { showToast } = useToast()

  const [loading, setLoading] = useState(false)

  const formSendCode = useForm<sendCodeForm>({
    resolver: zodResolver(sendCodeSchema)
  })

  const formChangePassword = useForm<changePasswordForm>({
    resolver: zodResolver(changePasswordScheme)
  })

  const handleSubmitSendCode = async (data: sendCodeForm) => {
    try {
      setLoading(true)
      await httpClientPost('/usuarios/recuperar-senha', data)
      showToast('Codigo enviado para o seu email.', 'success')
      formChangePassword.setValue('email', data.email)
      formSendCode.reset()
      setStep(1)
    } catch (error) {
      CatchMessageError(error)
    } finally {
      setLoading(false)
    }
  }
  const handleSubmitChangePassword = async (data: changePasswordForm) => {
    try {
      setLoading(true)
      await httpClientPost('/usuarios/alterar-senha', data)
      showToast('Senha alterada com sucesso', 'success')
      await new Promise((resolve) => setTimeout(resolve, 1000))
      navigateTo('/login')
    } catch (error) {
      CatchMessageError(error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex items-center justify-center flex-col w-full">
      <div className="max-w-96 w-full flex mb-4">
        <a onClick={() => navigateTo('/login')} className="flex gap-4 items-center max-w-96 text-left text-acelerait-black cursor-pointer">
          <IconLeft />
          Voltar
        </a>
      </div>

      {step == 0 && (
        <form onSubmit={formSendCode.handleSubmit(handleSubmitSendCode)} className="max-w-96 w-full flex flex-col m-auto ">
          <h3 className="text-3xl text-[#262626]">Esqueci minha senha</h3>
          <h5 className="mb-6 text-[#666] text-sm text-left">Continue abaixo para redefinir sua senha, digite seu email e vamos te enviar um código de redefinição</h5>
          <div className="flex flex-col gap-1.5">
            <label className="text-[#6B7280] text-sm">Email</label>
            <Input {...formSendCode.register('email')} placeholder="Email" />
            {formSendCode?.formState?.errors?.email && <ErrorMessageInput message={formSendCode?.formState?.errors?.email.message} />}
          </div>

          <Button type="submit" className="bg-acelerafit-green-200 hover:bg-acelerafit-green-50 text-muted mt-7" disabled={loading}>
            Enviar código
          </Button>
        </form>
      )}

      {step == 1 && (
        <form onSubmit={formChangePassword.handleSubmit(handleSubmitChangePassword)} className="max-w-96 w-full flex flex-col m-auto ">
          <h3 className="text-3xl text-[#262626]">Redefina sua senha</h3>
          <h5 className="mb-6 text-[#666] text-sm text-left"></h5>
          <div className="flex flex-col gap-1.5 mb-2">
            <label className="text-[#6B7280] text-sm">Email</label>
            <Input {...formChangePassword.register('email')} placeholder="Email" />
            {formChangePassword?.formState?.errors?.email && <ErrorMessageInput message={formChangePassword?.formState?.errors?.email.message} />}
          </div>

          <div className="flex flex-col gap-1.5 mb-2">
            <label className="text-[#6B7280] text-sm">Código</label>
            <Input {...formChangePassword.register('codigo')} placeholder="Código de redefinição" />
            {formChangePassword?.formState?.errors?.codigo && <ErrorMessageInput message={formChangePassword?.formState?.errors?.codigo.message} />}
          </div>

          <div className="flex flex-col gap-1.5 mb-2">
            <label className="text-[#6B7280] text-sm">Nova senha</label>
            <Input {...formChangePassword.register('novaSenha')} placeholder="Digite sua nova senha" />
            {formChangePassword?.formState?.errors?.novaSenha && <ErrorMessageInput message={formChangePassword?.formState?.errors?.novaSenha.message} />}
          </div>

          <Button type="submit" className="bg-acelerafit-green-200 hover:bg-acelerafit-green-50 text-muted mt-7" disabled={loading}>
            Redefinir
          </Button>
        </form>
      )}
    </div>
  )
}
